import type { ReactElement } from 'react';
import type { RecompressParamsDto, RecompressReportDto, StorageRecountReportDto } from '@gr/core/content/media/recompress';
import { Link, useT } from '../../../../app/di';
import { formatFileSize } from '../../../../shared/format';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { DataTable } from '../../../../ui-kit/DataTable/DataTable';
import { savedShare } from '../model/recompress-form';
import styles from './RecompressTaskDetails.module.scss';

export interface RecompressTaskDetailsProps {
  blogId: string;
  params: RecompressParamsDto;
  report: RecompressReportDto | null;
}

/** MED-UC-9, TASK-UC-3: параметры пережатия и итог — сколько пережато и сколько места освобождено. */
export function RecompressTaskDetails({ blogId, params, report }: RecompressTaskDetailsProps): ReactElement {
  const { t, te, locale } = useT();
  const size = (bytes: number): string => formatFileSize(bytes, locale);
  const px = (value: number | null): string => (value === null ? t('media.recompress.noResize') : t('media.recompress.px', { n: value }));
  const saved = report ? savedShare(report.bytesBefore, report.bytesAfter) : null;
  return (
    <div className="u-stack">
      <div className="u-stack u-gap-1">
        <strong>{t('media.recompress.paramsTitle')}</strong>
        <ul className={styles.list}>
          <li>{t('media.recompress.params.sources', { list: params.sources.map((ext) => ext.toUpperCase()).join(', ') })}</li>
          {params.image ? (
            <li>{t('media.recompress.params.image', { target: t(`media.recompress.targets.${params.image.target}`), quality: params.image.quality, size: px(params.image.maxLongSide) })}</li>
          ) : null}
          {params.video ? (
            <li>
              {t('media.recompress.params.video', {
                target: t(`media.recompress.targets.${params.video.target}`),
                crf: params.video.crf,
                size: px(params.video.maxShortSide),
                fps: params.video.maxFps === null ? t('media.recompress.sameFps') : t('media.recompress.fps', { n: params.video.maxFps }),
                kbps: t('media.recompress.kbps', { n: params.video.audioKbps }),
              })}
            </li>
          ) : null}
          <li>{t('media.recompress.params.minSavings', { n: params.minSavingsPercent })}</li>
        </ul>
      </div>
      {report ? (
        <div className="u-stack u-gap-1">
          <strong>{t('media.recompress.resultTitle')}</strong>
          {report.demo ? <Alert tone="info">{t('media.recompress.report.demo')}</Alert> : null}
          <ul className={styles.list}>
            <li>{t('media.recompress.report.converted', { n: report.converted })}</li>
            {report.notWorth > 0 ? <li>{t('media.recompress.report.notWorth', { n: report.notWorth })}</li> : null}
            {report.failed > 0 ? <li>{t('media.recompress.report.failed', { n: report.failed })}</li> : null}
            {report.skipped > 0 ? <li>{t('media.recompress.report.skipped', { n: report.skipped })}</li> : null}
            {report.converted > 0 ? <li>{t('media.recompress.report.volume', { before: size(report.bytesBefore), after: size(report.bytesAfter) })}</li> : null}
            {saved !== null ? <li className={styles.saved}>{t('media.recompress.report.saved', { size: size(report.bytesBefore - report.bytesAfter), percent: Math.round(saved * 100) })}</li> : null}
          </ul>
          {report.failures.length > 0 ? (
            <DataTable
              label={t('media.recompress.report.failures')}
              rows={report.failures}
              rowKey={(row) => row.mediaId}
              columns={[
                {
                  key: 'name',
                  header: t('media.recompress.report.file'),
                  primary: true,
                  cell: (row) => (
                    <Link to="mediaItem" params={{ blogId, mediaId: row.mediaId }}>
                      {row.name}
                    </Link>
                  ),
                },
                { key: 'reason', header: t('media.recompress.report.reason'), cell: (row) => te(row.code) },
              ]}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** MED-UC-10: итог пересчёта объёма медиа. */
export function StorageRecountDetails({ report }: { report: StorageRecountReportDto | null }): ReactElement | null {
  const { t } = useT();
  if (!report) return null;
  return (
    <ul className={styles.list}>
      <li>{t('media.recount.measured', { n: report.measured })}</li>
      {report.missing > 0 ? <li>{t('media.recount.missing', { n: report.missing })}</li> : null}
    </ul>
  );
}
